/**
 * StepprWizard.tsx - OpCentral wrapper for react-step-wizard
 * 
 * CHANGE HISTORY:
 * - 251101_1726 [pending] danielbeer: Initial creation wrapping react-step-wizard
 * 
 * Provides OpCentral-styled wizard component with:
 * - Consistent theming via Skinnr
 * - Standard navigation patterns
 * - Progress indicators
 * - Validation hooks
 * - Analytics integration
 */

import React from 'react';
import StepWizard from 'react-step-wizard';
import './StepprWizard.css';

export interface StepprWizardProps {
  children: React.ReactNode;
  initialStep?: number;
  isHashEnabled?: boolean;
  nav?: React.ReactElement;
  transitions?: {
    enterRight?: string;
    enterLeft?: string;
    exitRight?: string;
    exitLeft?: string;
    intro?: string;
  };
  onStepChange?: (stats: StepStats) => void;
  className?: string;
}

export interface StepStats {
  currentStep: number;
  totalSteps: number;
  isActive: boolean;
}

export interface StepProps {
  currentStep: number;
  totalSteps: number;
  isActive: boolean;
  previousStep: () => void;
  nextStep: () => void;
  goToStep: (step: number | string) => void;
  firstStep: () => void;
  lastStep: () => void;
  goToNamedStep: (name: string) => void;
  hashKey?: string;
}

/**
 * StepprWizard - Sequential step/wizard component
 * 
 * Wraps react-step-wizard with OpCentral styling and patterns.
 * Each child component becomes a step in the wizard.
 * 
 * @example
 * ```tsx
 * <StepprWizard initialStep={1} onStepChange={handleStepChange}>
 *   <WelcomeStep />
 *   <ConfigStep />
 *   <ReviewStep />
 *   <CompleteStep />
 * </StepprWizard>
 * ```
 */
export const StepprWizard: React.FC<StepprWizardProps> = ({
  children,
  initialStep = 1,
  isHashEnabled = false,
  nav,
  transitions,
  onStepChange,
  className = ''
}) => {
  return (
    <div className={`steppr-wizard ${className}`}>
      <StepWizard
        initialStep={initialStep}
        isHashEnabled={isHashEnabled}
        nav={nav}
        transitions={transitions}
        onStepChange={onStepChange}
      >
        {children}
      </StepWizard>
    </div>
  );
};

export default StepprWizard;
